import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardHeader,
  IconButton,
} from "@mui/material";
import {
  Business,
  LocationOn,
  AccountBalance,
  Save,
  Refresh,
} from "@mui/icons-material";
import { getUserProfile, saveProfileDetails } from "../../api/profileAPI";

const ProfileManagement: React.FC = () => {
  const [formData, setFormData] = useState({
    // Company Information
    companyName: "",
    phone: "",
    cin: "",
    pan: "",
    gst: "",
    gstStateCode: "",
    legalSignatory: "",

    // Addresses
    registeredAddress: "",
    sameAddress: false,
    factoryAddress: "",

    // Banking Details
    accountName: "",
    accountNo: "",
    bankName: "",
    branch: "",
    ifsc: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Load user profile data on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setLoading(true);
        const response = await getUserProfile();
        const profileData =
          response?.data?.profile_details || response?.profile_details;

        if (profileData) {
          setFormData({
            companyName: profileData.companyName || "",
            phone: profileData.phoneNumber || "",
            cin: profileData.cin || "",
            pan: profileData.pan || "",
            gst: profileData.gstNumber || "",
            gstStateCode: profileData.gstStateCode || "",
            legalSignatory: profileData.legalSignatory || "",
            registeredAddress: profileData.registeredAddress || "",
            sameAddress: profileData.isFactorySameAsRegistered || false,
            factoryAddress: profileData.factoryAddress || "",
            accountName: profileData.accountName || "",
            accountNo: profileData.accountNumber || "",
            bankName: profileData.bankName || "",
            branch: profileData.bankBranch || "",
            ifsc: profileData.ifscCode || "",
          });
        }
      } catch (error) {
        console.error("Error loading user profile:", error);
        // Show error toast if needed
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  const requiredFields = [
    "companyName",
    "phone",
    "pan",
    "legalSignatory",
    "registeredAddress",
    "accountName",
    "accountNo",
    "bankName",
    "ifsc",
  ];

  const states = [
    { value: "01", label: "01 – Jammu & Kashmir" },
    { value: "02", label: "02 – Himachal Pradesh" },
    { value: "03", label: "03 – Punjab" },
    { value: "04", label: "04 – Chandigarh" },
    { value: "05", label: "05 – Uttarakhand" },
    { value: "06", label: "06 – Haryana" },
    { value: "07", label: "07 – Delhi" },
    { value: "08", label: "08 – Rajasthan" },
    { value: "09", label: "09 – Uttar Pradesh" },
    { value: "10", label: "10 – Bihar" },
    { value: "11", label: "11 – Sikkim" },
    { value: "12", label: "12 – Arunachal Pradesh" },
    { value: "13", label: "13 – Nagaland" },
    { value: "14", label: "14 – Manipur" },
    { value: "15", label: "15 – Mizoram" },
    { value: "16", label: "16 – Tripura" },
    { value: "17", label: "17 – Meghalaya" },
    { value: "18", label: "18 – Assam" },
    { value: "19", label: "19 – West Bengal" },
    { value: "20", label: "20 – Jharkhand" },
    { value: "21", label: "21 – Odisha" },
    { value: "22", label: "22 – Chhattisgarh" },
    { value: "23", label: "23 – Madhya Pradesh" },
    { value: "24", label: "24 – Gujarat" },
    { value: "25", label: "25 – Daman & Diu" },
    { value: "26", label: "26 – Dadra & Nagar Haveli" },
    { value: "27", label: "27 – Maharashtra" },
    { value: "29", label: "29 – Karnataka" },
    { value: "30", label: "30 – Goa" },
    { value: "31", label: "31 – Lakshadweep" },
    { value: "32", label: "32 – Kerala" },
    { value: "33", label: "33 – Tamil Nadu" },
    { value: "34", label: "34 – Puducherry" },
    { value: "35", label: "35 – Andaman & Nicobar" },
    { value: "36", label: "36 – Telangana" },
    { value: "37", label: "37 – Andhra Pradesh" },
  ];

  const handleInputChange = (field: string, value: string) => {
    // Phone number validation - only numbers and + sign
    if (field === "phone") {
      const phoneValue = value.replace(/[^0-9+]/g, "");
      setFormData((prev) => ({ ...prev, [field]: phoneValue }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    // Account number validation - only numbers
    if (field === "accountNo") {
      const accountValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({ ...prev, [field]: accountValue }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
      factoryAddress: checked ? prev.registeredAddress : "",
    }));
  };

  const fieldValidations: Record<
    string,
    { required?: boolean; regex?: RegExp; message: string }[]
  > = {
    companyName: [
      { required: true, message: "Company name is required" },
      {
        regex: /^[a-zA-Z0-9\s\-&.]{2,100}$/,
        message: "Enter a valid Company Name.",
      },
    ],
    phone: [
      { required: true, message: "Phone number is required" },
      {
        regex: /^(91)?[6-9]\d{9}$/,
        message: "Enter a valid 10-digit mobile number starting with 6\u20139.",
      },
    ],
    cin: [
      {
        regex: /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
        message:
          "Enter a valid 21-character CIN number (Example: U12345MH2010PTC123456).",
      },
    ],
    pan: [
      { required: true, message: "PAN number is required" },
      {
        regex: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        message:
          "Enter a valid PAN number in uppercase format (Example: ABCDE1234F).",
      },
    ],
    gst: [
      {
        regex: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        message:
          "Enter a valid 15-character GST number (Example: 27ABCDE1234F1Z5).",
      },
    ],
    legalSignatory: [
      { required: true, message: "Legal signatory is required" },
      {
        regex: /^[a-zA-Z\s.'-]{2,100}$/,
        message: "Enter a valid Legal Signatory.",
      },
    ],
    registeredAddress: [
      { required: true, message: "Registered address is required" },
    ],
    accountName: [
      { required: true, message: "Account name is required" },
      {
        regex: /^[a-zA-Z0-9\s\-'&]{2,100}$/,
        message: "Enter a valid Account Name.",
      },
    ],
    accountNo: [
      { required: true, message: "Account number is required" },
      {
        regex: /^\d{9,18}$/,
        message:
          "Enter a valid bank account number containing 9\u201318 digits.",
      },
    ],
    bankName: [
      { required: true, message: "Bank name is required" },
      {
        regex: /^[a-zA-Z0-9\s\-&]{2,100}$/,
        message: "Enter a valid Bank Name.",
      },
    ],
    branch: [
      { regex: /^[a-zA-Z0-9\s\-,.]{2,100}$/, message: "Enter a valid Branch." },
    ],
    ifsc: [
      { required: true, message: "IFSC code is required" },
      {
        regex: /^[A-Z]{4}0[A-Z0-9]{6}$/,
        message: "Enter a valid IFSC code (Example: MAHB0001821).",
      },
    ],
  };

  const handleBlur = (field: string, value: string) => {
    const rules = fieldValidations[field];
    if (!rules) return;
    const trimmedValue = value.trim();
    for (const rule of rules) {
      if (rule.required && !trimmedValue) {
        setErrors((prev) => ({ ...prev, [field]: rule.message }));
        return;
      }
      if (rule.regex && trimmedValue && !rule.regex.test(trimmedValue)) {
        setErrors((prev) => ({ ...prev, [field]: rule.message }));
        return;
      }
    }
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};

    Object.entries(fieldValidations).forEach(([field, rules]) => {
      const value = String(
        formData[field as keyof typeof formData] || ""
      ).trim();
      for (const rule of rules) {
        if (rule.required && !value) {
          newErrors[field] = rule.message;
          break;
        }
        if (rule.regex && value && !rule.regex.test(value)) {
          newErrors[field] = rule.message;
          break;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setValidationErrors(Object.values(newErrors));
      return;
    }

    try {
      setLoading(true);

      // Prepare data for API call
      const profileData = {
        companyName: formData.companyName,
        phoneNumber: formData.phone,
        cin: formData.cin,
        pan: formData.pan,
        gstNumber: formData.gst,
        gstStateCode: formData.gstStateCode,
        legalSignatory: formData.legalSignatory,
        registeredAddress: formData.registeredAddress,
        factoryAddress: formData.sameAddress
          ? formData.registeredAddress
          : formData.factoryAddress,
        isFactorySameAsRegistered: formData.sameAddress,
        accountName: formData.accountName,
        accountNumber: formData.accountNo,
        bankName: formData.bankName,
        bankBranch: formData.branch,
        ifscCode: formData.ifsc,
      };

      await saveProfileDetails(profileData);
      setShowToast(true);
      setValidationErrors([]); // Clear errors after successful save
    } catch (error) {
      console.error("Error saving profile details:", error);
      setValidationErrors([
        "Failed to save profile details. Please try again.",
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getFieldError = (fieldName: string) => {
    return errors[fieldName] || "";
  };

  const handleReset = () => {
    setFormData({
      companyName: "",
      phone: "",
      cin: "",
      pan: "",
      gst: "",
      gstStateCode: "",
      legalSignatory: "",
      registeredAddress: "",
      sameAddress: false,
      factoryAddress: "",
      accountName: "",
      accountNo: "",
      bankName: "",
      branch: "",
      ifsc: "",
    });
    setErrors({});
  };

  return (
    <Box
      sx={{
        maxWidth: 860,
        mx: "auto",
        p: 3,
        bgcolor: "#f5f5f3",
        minHeight: "100vh",
      }}
    >
      {/* Page Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Business sx={{ fontSize: 22, color: "#5f5e5a" }} />
        <Typography
          variant="h6"
          sx={{ fontSize: 20, fontWeight: 600, color: "#1a1a18" }}
        >
          Seller details
        </Typography>
      </Box>

      {/* Company Information */}
      <Card
        sx={{
          mb: 2,
          border: "1px solid #e2e0d8",
          borderRadius: 3,
          boxShadow: "none",
        }}
      >
        <CardHeader
          sx={{
            bgcolor: "#faf9f7",
            borderBottom: "1px solid #e2e0d8",
            "& .MuiCardHeader-content": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          avatar={<Business sx={{ fontSize: 16, color: "#888780" }} />}
          title={
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#2c2c2a",
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Company information
            </Typography>
          }
        />
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Company name <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Acme Pvt. Ltd."
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange("companyName", e.target.value)
                }
                onBlur={(e) => handleBlur("companyName", e.target.value)}
                error={!!getFieldError("companyName")}
                helperText={getFieldError("companyName")}
                inputProps={{ style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Phone no. <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="+91 XXXXX XXXXX"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                onBlur={(e) => handleBlur("phone", e.target.value)}
                error={!!getFieldError("phone")}
                helperText={getFieldError("phone")}
                inputProps={{ maxLength: 12, style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                CIN no.
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. U12345MH2010PTC123456"
                value={formData.cin}
                onChange={(e) => handleInputChange("cin", e.target.value)}
                onBlur={(e) => handleBlur("cin", e.target.value.toUpperCase())}
                error={!!getFieldError("cin")}
                helperText={getFieldError("cin")}
                inputProps={{ maxLength: 21, style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                PAN no. <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. ABCDE1234F"
                value={formData.pan.toUpperCase()}
                onChange={(e) =>
                  handleInputChange("pan", e.target.value.toUpperCase())
                }
                onBlur={(e) => handleBlur("pan", e.target.value.toUpperCase())}
                inputProps={{ maxLength: 10, style: { fontSize: "13px" } }}
                error={!!getFieldError("pan")}
                helperText={getFieldError("pan")}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                GST no.
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 27ABCDE1234F1Z5"
                value={formData.gst.toUpperCase()}
                onChange={(e) =>
                  handleInputChange("gst", e.target.value.toUpperCase())
                }
                onBlur={(e) => handleBlur("gst", e.target.value.toUpperCase())}
                error={!!getFieldError("gst")}
                helperText={getFieldError("gst")}
                inputProps={{ maxLength: 15, style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                GST state code
              </Typography>
              <FormControl
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              >
                <Select
                  value={formData.gstStateCode}
                  onChange={(e) =>
                    handleInputChange("gstStateCode", e.target.value)
                  }
                  displayEmpty
                  sx={{ fontSize: "13px" }}
                >
                  <MenuItem value="">Select state</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Legal signatory <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Full name of authorized person"
                value={formData.legalSignatory}
                onChange={(e) =>
                  handleInputChange("legalSignatory", e.target.value)
                }
                onBlur={(e) => handleBlur("legalSignatory", e.target.value)}
                error={!!getFieldError("legalSignatory")}
                helperText={getFieldError("legalSignatory")}
                inputProps={{ style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Addresses */}
      <Card
        sx={{
          mb: 2,
          border: "1px solid #e2e0d8",
          borderRadius: 3,
          boxShadow: "none",
        }}
      >
        <CardHeader
          sx={{
            bgcolor: "#faf9f7",
            borderBottom: "1px solid #e2e0d8",
            "& .MuiCardHeader-content": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          avatar={<LocationOn sx={{ fontSize: 16, color: "#888780" }} />}
          title={
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#2c2c2a",
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Addresses
            </Typography>
          }
        />
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Registered address <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Street, city, state, PIN code"
                value={formData.registeredAddress}
                onChange={(e) => {
                  handleInputChange("registeredAddress", e.target.value);
                  if (formData.sameAddress) {
                    handleInputChange("factoryAddress", e.target.value);
                  }
                }}
                onBlur={(e) => handleBlur("registeredAddress", e.target.value)}
                error={!!getFieldError("registeredAddress")}
                helperText={getFieldError("registeredAddress")}
                inputProps={{ style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.sameAddress}
                    onChange={(e) =>
                      handleCheckboxChange("sameAddress", e.target.checked)
                    }
                    sx={{ "&.Mui-checked": { color: "#378add" } }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: "#5f5e5a" }}>
                    Factory address is the same as registered address
                  </Typography>
                }
              />
            </Grid>
            {!formData.sameAddress && (
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#5f5e5a",
                    mb: 0.5,
                  }}
                >
                  Factory address
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Street, city, state, PIN code"
                  value={formData.factoryAddress}
                  onChange={(e) =>
                    handleInputChange("factoryAddress", e.target.value)
                  }
                  inputProps={{ style: { fontSize: "13px" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                  }}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Banking Details */}
      <Card
        sx={{
          mb: 2,
          border: "1px solid #e2e0d8",
          borderRadius: 3,
          boxShadow: "none",
        }}
      >
        <CardHeader
          sx={{
            bgcolor: "#faf9f7",
            borderBottom: "1px solid #e2e0d8",
            "& .MuiCardHeader-content": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          avatar={<AccountBalance sx={{ fontSize: 16, color: "#888780" }} />}
          title={
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#2c2c2a",
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Banking details
            </Typography>
          }
        />
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Account name <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="As per bank records"
                value={formData.accountName}
                onChange={(e) =>
                  handleInputChange("accountName", e.target.value)
                }
                onBlur={(e) => handleBlur("accountName", e.target.value)}
                error={!!getFieldError("accountName")}
                helperText={getFieldError("accountName")}
                inputProps={{ style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Account no. <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 000123456789"
                value={formData.accountNo}
                onChange={(e) => handleInputChange("accountNo", e.target.value)}
                onBlur={(e) => handleBlur("accountNo", e.target.value)}
                error={!!getFieldError("accountNo")}
                helperText={getFieldError("accountNo")}
                inputProps={{ maxLength: 18, style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Bank name <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. HDFC Bank"
                value={formData.bankName}
                onChange={(e) => handleInputChange("bankName", e.target.value)}
                onBlur={(e) => handleBlur("bankName", e.target.value)}
                error={!!getFieldError("bankName")}
                helperText={getFieldError("bankName")}
                inputProps={{ style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                Branch
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Andheri West, Mumbai"
                value={formData.branch}
                onChange={(e) => handleInputChange("branch", e.target.value)}
                onBlur={(e) => handleBlur("branch", e.target.value)}
                error={!!getFieldError("branch")}
                helperText={getFieldError("branch")}
                inputProps={{ style: { fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5f5e5a",
                  mb: 0.5,
                }}
              >
                IFSC code <span style={{ color: "#c0392b" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. HDFC0001234"
                value={formData.ifsc.toUpperCase()}
                onChange={(e) =>
                  handleInputChange("ifsc", e.target.value.toUpperCase())
                }
                onBlur={(e) => handleBlur("ifsc", e.target.value.toUpperCase())}
                inputProps={{ maxLength: 11, style: { fontSize: "13px" } }}
                error={!!getFieldError("ifsc")}
                helperText={getFieldError("ifsc")}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  "& .MuiInputBase-input::placeholder": { fontSize: "12px" },
                }}
              />
              <Typography sx={{ fontSize: 11, color: "#b4b2a9", mt: 0.5 }}>
                11-character alphanumeric code
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mt: 3,
          justifyContent: "flex-start",
        }}
      >
        <Button
          variant="contained"
          startIcon={<Save sx={{ fontSize: 14 }} />}
          onClick={handleSave}
          disabled={loading}
          sx={{
            bgcolor: "#F97316",
            color: "white",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            padding: "6px 12px",
            minWidth: "auto",
            "&:hover": { bgcolor: "#EA580C" },
            "&:disabled": { bgcolor: "#ccc", color: "#666" },
          }}
        >
          {loading ? "Saving..." : "Save details"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh sx={{ fontSize: 14 }} />}
          onClick={handleReset}
          sx={{
            borderColor: "#d0cec6",
            color: "#2c2c2a",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            padding: "6px 12px",
            minWidth: "auto",
            backgroundColor: "white",
            "&:hover": {
              bgcolor: "#f5f5f3",
              borderColor: "#d0cec6",
            },
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Validation Errors Display */}
      {validationErrors.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#856404", mb: 1, fontSize: "16px", fontWeight: 600 }}
          >
            Please fix the following validation errors:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, color: "#856404" }}>
            {validationErrors.map((error, index) => (
              <Typography
                component="li"
                key={index}
                sx={{ mb: 0.5, fontSize: "14px" }}
              >
                {error}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* Success Toast */}
      <Snackbar
        open={showToast}
        autoHideDuration={3000}
        onClose={() => setShowToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          mt: "70px",
        }}
      >
        <Alert
          onClose={() => setShowToast(false)}
          severity="success"
          sx={{
            bgcolor: "#eaf3de",
            color: "#27500a",
            border: "1px solid #97c459",
            "& .MuiAlert-icon": { color: "#27500a" },
          }}
        >
          Details saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileManagement;
